import * as React from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import {
  getTerminalCalls,
  updateTerminalCall,
} from "../../data/terminalcall";
import { useDispatch, useSelector } from "react-redux";
import { TerminalCall } from "../../types/TerminalCall";
import { DateTimeField } from "../FormFields/RenderDateTimeField";
import { hideDialog } from "../../data/dialog";
import { lighten } from '@mui/material/styles';

import { Button, Divider, Grid, Tooltip, Typography, Alert } from "@mui/material";
import { updateSelectedContainer } from "../../data/selectedcontainers";

import {
  Check as CheckIcon,
  CheckCircle as CheckCircleIcon,
  FileOpen as FileOpenIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

const MobileTerminalCallCompletionForm = ({
  terminalCall,
}) => {
  const dispatch = useDispatch();
  const current = new Date();

  const {
    selectedContainers: { data: filteredContainers },
  } = useSelector((state: any) => ({
    selectedContainers: state.selectedContainers,
  }));

  const loadingContainers = filteredContainers.filter(c => terminalCall.loadingContainers.some(container => container['@id'] === c['@id']));
  const unloadingContainers = filteredContainers.filter(c => terminalCall.unloadingContainers.some(container => container['@id'] === c['@id']));

  const { handleSubmit, control, formState } = useForm({
    defaultValues: {
      ...terminalCall,
      status: "Completed",
      completed: true,
      arrival: terminalCall.arrival ?? current,
      departure: terminalCall.departure ?? current,
      operationStart: terminalCall.operationStart ?? current,
      operationEnd: terminalCall.operationEnd ?? current,
    },
  });

  const { isSubmitting } = formState;

  const onSubmit: SubmitHandler<TerminalCall> = (data) => {
    dispatch(updateTerminalCall(data)).then(() => dispatch(getTerminalCalls()));
  };

  const allLoaded = loadingContainers.length > 0 && loadingContainers.every(c => c.loadedDate);
  const allUnloaded = unloadingContainers.length > 0 && unloadingContainers.every(c => c.unloadedDate);

  const handleMarkAllLoaded = () => {
    loadingContainers.forEach((container) => {
      if (container.loadedDate) return;
  
      dispatch(
        updateSelectedContainer({
          ...container,
          loadedDate: current.toISOString(),
        }, false)
      );
    });
  };
  
  const handleMarkAllUnloaded = () => {
    unloadingContainers.forEach((container) => {
      if (container.unloadedDate) return;
  
      dispatch(
        updateSelectedContainer({
          ...container,
          unloadedDate: current.toISOString(),
        }, false)
      );
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container>
        <Grid item xs={12} sm={12}>
          <Grid item xs={12}>
            <Grid item xs={12} className="form-fields-container">
              <Grid item xs={12} sm={12} className="form-field-datetime">
                <DateTimeField
                  name="arrival"
                  label="Arrival"
                  control={control}
                  required
                />
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              className="form-fields-container"
              ml={4}
              mt={4}
            >
              <Grid item xs={12} sm={12} className="form-field-datetime">
                <DateTimeField
                  name="operationStart"
                  label="Started"
                  control={control}
                  required
                />
              </Grid>
            </Grid>
            <Grid item xs={12} className="form-fields-container" ml={4}>
              <Grid item xs={12} sm={12} className="form-field-datetime">
                <DateTimeField
                  name="operationEnd"
                  label="Finished"
                  control={control}
                  required
                />
              </Grid>
            </Grid>
            <Grid item xs={12} className="form-fields-container" mt={4}>
              <Grid item xs={12} sm={12} className="form-field-datetime">
                <DateTimeField
                  name="departure"
                  label="Departure"
                  control={control}
                  required
                />
              </Grid>
            </Grid>
            <Divider />
            <Grid container gap={2} flexWrap="nowrap" mt={2}>
              <Grid
                item
                xs={6}
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
              >
                <Typography variant="h6">{`Loading ${loadingContainers.length > 0 ? `(${loadingContainers.length})`: ""}`}</Typography>
                {loadingContainers.length === 0 ? (
                  <Alert severity="info">
                    No containers to be loaded in this terminal call
                  </Alert>
                ) : (
                  <>
                  { !allLoaded && loadingContainers.length > 1 ? (
                    <Alert severity="warning" sx={{ mb: 1, width: "100%" }}>
                      Not all containers were marked as loaded.
                    </Alert>
                  ) : ""}
                  <Grid container maxHeight="30vh" alignItems="flex-start" overflow="auto">
                    {loadingContainers.map((c) => (
                      <Grid
                        key={c["@id"]}
                        item
                        width="100%"
                        sx={{
                          border: "1px solid #DDD",
                          p: 0.5,
                          mb: 0.5,
                          bgcolor: c.loadedDate ? lighten("#28A745", 0.7) : lighten("#FFC107", 0.7),
                          borderRadius: 1,
                          position: "relative",
                          "&:hover .select-checkbox, .select-checkbox.Mui-checked": {
                            display: "flex",
                          },
                        }}
                      >
                        <Grid item sx={{ display: "flex", gap: 1 }}>
                          <Grid className="logistic-container">
                            <Typography
                              display="flex"
                              alignItems="center"
                              gap={1}
                              component="span"
                              variant="body2"
                              color="text.primary"
                              noWrap
                              fontWeight={700}
                              justifyContent="space-between"
                            >
                              {c.uid}
                              {c.importFile && (
                                <Tooltip title={c.importFile}>
                                  <FileOpenIcon
                                    fontSize="14px"
                                    sx={{ color: "#008000" }}
                                  />
                                </Tooltip>
                              )}
                            </Typography>
                            <small>
                              {c.containerType?.name
                                ? `${c.containerType.name} | `
                                : ""}
                              {c.weight ? `${c.weight}kg` : ""}
                            </small>
                          </Grid>
                          <Grid
                            container
                            alignItems="center"
                            justifyContent="flex-end"
                          >
                            { c.loadedDate && (
                              <Tooltip title="Loaded to a terminal">
                                <CheckCircleIcon
                                  color="success"
                                  fontSize="inherit"
                                  sx={{
                                    backgroundColor: "#FFFFFF",
                                    borderRadius: "100%",
                                  }}
                                />
                              </Tooltip>
                            ) }
                          </Grid>
                        </Grid>
                      </Grid>
                    ))}
                  </Grid>
                  { allLoaded ? (
                      <Alert severity="success" sx={{ mt: 1, width: "100%" }}>
                        All containers have been loaded.
                      </Alert>
                    ) : (
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleMarkAllLoaded}
                      sx={{ mt: 1 }}
                    >
                      Mark as loaded
                    </Button>
                    )
                  }
                  </>
                )}
              </Grid>
              <Grid
                item
                xs={6}
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
              >
                <Typography variant="h6">{`Unloading ${unloadingContainers.length > 0 ? `(${unloadingContainers.length})`: ""}`}</Typography>
                {unloadingContainers.length === 0 ? (
                  <Alert severity="info">
                    No containers to be unloaded in this terminal call
                  </Alert>
                ) : (
                  <>
                  { !allUnloaded && unloadingContainers.length > 1 ? (
                    <Alert severity="warning" sx={{ mb: 1, width: "100%" }}>
                      Not all containers were marked as unloaded.
                    </Alert>
                  ) : ""}
                  <Grid container maxHeight="30vh" alignItems="flex-start" overflow="auto">
                    {unloadingContainers.map((c) => (
                      <Grid
                        key={c["@id"]}
                        item
                        width="100%"
                        sx={{
                          border: "1px solid #DDD",
                          p: 0.5,
                          mb: 0.5,
                          bgcolor: c.unloadedDate ? lighten("#28A745", 0.7) : lighten("#FFC107", 0.7),
                          borderRadius: 1,
                          position: "relative",
                          "&:hover .select-checkbox, .select-checkbox.Mui-checked": {
                            display: "flex",
                          },
                        }}
                      >
                        <Grid item sx={{ display: "flex", gap: 1 }}>
                          <Grid className="logistic-container">
                            <Typography
                              display="flex"
                              alignItems="center"
                              gap={1}
                              component="span"
                              variant="body2"
                              color="text.primary"
                              noWrap
                              fontWeight={700}
                              justifyContent="space-between"
                            >
                              {c.uid}
                              {c.importFile && (
                                <Tooltip title={c.importFile}>
                                  <FileOpenIcon
                                    fontSize="14px"
                                    sx={{ color: "#008000" }}
                                  />
                                </Tooltip>
                              )}
                            </Typography>
                            <small>
                              {c.containerType?.name
                                ? `${c.containerType.name} | `
                                : ""}
                              {c.weight ? `${c.weight}kg` : ""}
                            </small>
                          </Grid>
                          <Grid
                            container
                            alignItems="center"
                            justifyContent="flex-end"
                          >
                            { c.unloadedDate && (
                              <Tooltip title="Loaded to a terminal">
                                <CheckCircleIcon
                                  color="success"
                                  fontSize="inherit"
                                  sx={{
                                    backgroundColor: "#FFFFFF",
                                    borderRadius: "100%",
                                  }}
                                />
                              </Tooltip>
                            ) }
                          </Grid>
                        </Grid>
                      </Grid>
                    ))}
                  </Grid>
                  {  allUnloaded ? (
                    <Alert severity="success" sx={{ mt: 1, width: "100%" }}>
                      All containers have been unloaded.
                    </Alert>
                  ) : (
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleMarkAllUnloaded}
                      sx={{ mt: 1 }}
                    >
                      Mark as unloaded
                    </Button>
                  )}
                  </>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid
        item
        xs={12}
        className="button-wrapper"
        sx={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: '#fff',
          padding: 2,
          zIndex: 10,
          borderTop: '2px solid #e0e0e0',
        }}
      >
        <Button
          onClick={() => {
            dispatch(hideDialog());
          }}
          disabled={isSubmitting}
          variant="contained"
        >
          <CloseIcon fontSize="inherit" />
          &nbsp;Close
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          variant="contained"
          sx={{ ml: 1 }}
        >
          <CheckIcon fontSize="inherit" />
          &nbsp;Mark as completed
        </Button>
      </Grid>
    </form>
  );
};

export default MobileTerminalCallCompletionForm;